// Updated: 2026-03-28 07:41:37
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, Mail, MapPin, Send, Upload, CheckCircle, AlertCircle, Calendar, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import CandidateIntakeForm from '../components/CandidateIntakeForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [formType, setFormType] = useState('general');
  const [showIntakeForm, setShowIntakeForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    howDidYouHear: ''
  });
  const [referralData, setReferralData] = useState({
    referrerName: '',
    referrerEmail: '',
    referrerPhone: '',
    companyName: '',
    companyLocation: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactTitle: '',
    hiringNeeds: '',
    additionalInfo: '',
    howDidYouHear: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const type = searchParams.get('type');
    if (type === 'hire') {
      setFormType('hire');
    } else if (type === 'resume') {
      setFormType('resume');
      setShowIntakeForm(true);
    } else if (type === 'referral') {
      setFormType('referral');
    } else {
      setFormType('general');
    }
  }, [searchParams]);

  // Load Calendly widget script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReferralInputChange = (e) => {
    const { name, value } = e.target;
    setReferralData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message,
        form_type: formType,
        howDidYouHear: formData.howDidYouHear
      });

      if (response.data.success) {
        setSubmitSuccess(true);
      }

      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          howDidYouHear: ''
        });
      }, 3000);

    } catch (error) {
      setSubmitError(error.response?.data?.detail || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReferralSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await axios.post(`${API}/contact`, {
        name: referralData.referrerName,
        email: referralData.referrerEmail,
        phone: referralData.referrerPhone,
        company: referralData.companyName,
        message: `COMPANY REFERRAL SUBMISSION

REFERRER INFORMATION:
Name: ${referralData.referrerName}
Email: ${referralData.referrerEmail}
Phone: ${referralData.referrerPhone}

REFERRED COMPANY:
Company Name: ${referralData.companyName}
Location: ${referralData.companyLocation}

COMPANY CONTACT:
Name: ${referralData.contactName}
Title: ${referralData.contactTitle}
Email: ${referralData.contactEmail}
Phone: ${referralData.contactPhone}

Hiring Needs / Positions:
${referralData.hiringNeeds}

Additional Information:
${referralData.additionalInfo}`,
        form_type: 'referral',
        howDidYouHear: referralData.howDidYouHear
      });

      if (response.data.success) {
        setSubmitSuccess(true);
      }

      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setReferralData({
          referrerName: '',
          referrerEmail: '',
          referrerPhone: '',
          companyName: '',
          companyLocation: '',
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          contactTitle: '',
          hiringNeeds: '',
          additionalInfo: '',
          howDidYouHear: ''
        });
      }, 3000);

    } catch (error) {
      setSubmitError(error.response?.data?.detail || 'Failed to submit referral. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 pt-32 bg-[#0a1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 break-words px-4">Contact Us</h1>
            <p className="text-lg sm:text-xl text-gray-300 px-4">
              Let's build your team together
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Information */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <h2 className="text-3xl font-bold text-[#0a1628] mb-6">Get In Touch</h2>
                  <p className="text-gray-600 mb-8">
                    Ready to hire top talent or advance your career? Reach out today for a free 15-minute discovery call.
                  </p>

                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#d4a574] rounded-lg flex items-center justify-center">
                        <Phone className="text-[#0a1628]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0a1628] mb-1">Phone</h3>
                        <a href="tel:513-668-0344" className="text-gray-600 hover:text-[#d4a574] transition-colors">
                          513-668-0344
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#d4a574] rounded-lg flex items-center justify-center">
                        <Mail className="text-[#0a1628]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0a1628] mb-1">Email</h3>
                        <a href="mailto:rich@s2stalent.com" className="text-gray-600 hover:text-[#d4a574] transition-colors">
                          rich@s2stalent.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#d4a574] rounded-lg flex items-center justify-center">
                        <MapPin className="text-[#0a1628]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0a1628] mb-1">Location</h3>
                        <p className="text-gray-600">
                          Batesville, Indiana<br />
                          <span className="text-sm">Serving Cincinnati, Indianapolis, Tri-State Region & Nationwide</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 italic">
                      "Call to schedule a free 15-minute discovery!"
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Forms */}
              <div className="lg:col-span-2">
                {/* Show intake form or regular forms */}
                {formType === 'resume' && showIntakeForm ? (
                  <Card className="border-2 border-gray-200">
                    <CardHeader className="bg-[#0a1628] text-white">
                      <CardTitle className="text-2xl">Candidate Intake Form</CardTitle>
                      <p className="text-sm text-gray-300 mt-2">
                        Please complete all sections to help us match you with the best opportunities
                      </p>
                    </CardHeader>
                    <CardContent className="p-8">
                      <CandidateIntakeForm onBack={() => setShowIntakeForm(false)} />
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Form Type Selector */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                      <Button
                        onClick={() => {
                          setFormType('hire');
                          setShowIntakeForm(false);
                          setSubmitSuccess(false);
                        }}
                        className={`${formType === 'hire' ? 'bg-[#d4a574] text-[#0a1628]' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        I Want to Hire Talent
                      </Button>
                      <Button
                        onClick={() => {
                          setFormType('resume');
                          setShowIntakeForm(true);
                          setSubmitSuccess(false);
                        }}
                        className={`${formType === 'resume' ? 'bg-[#d4a574] text-[#0a1628]' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        I'm Looking for a Job
                      </Button>
                      <Button
                        onClick={() => {
                          setFormType('referral');
                          setShowIntakeForm(false);
                          setSubmitSuccess(false);
                        }}
                        className={`${formType === 'referral' ? 'bg-[#d4a574] text-[#0a1628]' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        <Users className="mr-2" size={18} />
                        Submit a Referral
                      </Button>
                      <Button
                        onClick={() => {
                          setFormType('general');
                          setShowIntakeForm(false);
                          setSubmitSuccess(false);
                        }}
                        className={`${formType === 'general' ? 'bg-[#d4a574] text-[#0a1628]' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        General Inquiry
                      </Button>
                    </div>

                    <Card className="border-2 border-gray-200">
                      <CardHeader className="bg-[#0a1628] text-white">
                        <CardTitle className="text-2xl">
                          {formType === 'hire' && 'Employer Inquiry'}
                          {formType === 'general' && 'General Contact Form'}
                          {formType === 'referral' && (
                            <div className="flex items-center">
                              <Users className="mr-3" size={28} />
                              Submit a Referral and Get Paid!
                            </div>
                          )}
                        </CardTitle>
                        {formType === 'referral' && (
                          <p className="text-sm text-[#d4a574] mt-2">
                            Know a company that's hiring? Refer them to S2S and get paid when we make a placement!
                          </p>
                        )}
                      </CardHeader>
                      <CardContent className="p-8">
                        {submitSuccess ? (
                          <div className="text-center py-12">
                            <CheckCircle className="text-green-500 mx-auto mb-4" size={64} />
                            <h3 className="text-2xl font-bold text-[#0a1628] mb-2">Thank You!</h3>
                            <p className="text-gray-600">
                              {formType === 'hire' && "We'll be in touch shortly to discuss your hiring needs."}
                              {formType === 'general' && "We've received your message and will respond soon."}
                              {formType === 'referral' && "Your company referral has been submitted! We'll reach out to them and keep you updated on progress."}
                            </p>
                          </div>
                        ) : formType === 'referral' ? (
                          <form onSubmit={handleReferralSubmit} className="space-y-6">
                            {submitError && (
                              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
                                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                                <span>{submitError}</span>
                              </div>
                            )}

                            {/* Referrer Information */}
                            <div className="bg-gray-50 p-6 rounded-lg">
                              <h3 className="text-lg font-bold text-[#0a1628] mb-4 flex items-center">
                                <span className="bg-[#d4a574] text-[#0a1628] rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">1</span>
                                Your Information (Referrer)
                              </h3>
                              <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                  <Label htmlFor="referrerName">Your Name *</Label>
                                  <Input
                                    id="referrerName"
                                    name="referrerName"
                                    type="text"
                                    required
                                    value={referralData.referrerName}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="referrerEmail">Your Email *</Label>
                                  <Input
                                    id="referrerEmail"
                                    name="referrerEmail"
                                    type="email"
                                    required
                                    value={referralData.referrerEmail}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="referrerPhone">Your Phone *</Label>
                                  <Input
                                    id="referrerPhone"
                                    name="referrerPhone"
                                    type="tel"
                                    required
                                    value={referralData.referrerPhone}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Company Information */}
                            <div className="bg-[#0a1628]/5 p-6 rounded-lg border-2 border-[#d4a574]">
                              <h3 className="text-lg font-bold text-[#0a1628] mb-4 flex items-center">
                                <span className="bg-[#d4a574] text-[#0a1628] rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">2</span>
                                Company You're Referring
                              </h3>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="companyName">Company Name *</Label>
                                  <Input
                                    id="companyName"
                                    name="companyName"
                                    type="text"
                                    required
                                    value={referralData.companyName}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="companyLocation">Company Location</Label>
                                  <Input
                                    id="companyLocation"
                                    name="companyLocation"
                                    type="text"
                                    placeholder="City, State"
                                    value={referralData.companyLocation}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                              
                              <h4 className="text-md font-semibold text-[#0a1628] mt-6 mb-3">Company Contact (if known)</h4>
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="contactName">Contact Name</Label>
                                  <Input
                                    id="contactName"
                                    name="contactName"
                                    type="text"
                                    value={referralData.contactName}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="contactTitle">Contact Title</Label>
                                  <Input
                                    id="contactTitle"
                                    name="contactTitle"
                                    type="text"
                                    placeholder="e.g., HR Manager, Plant Manager"
                                    value={referralData.contactTitle}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="contactEmail">Contact Email</Label>
                                  <Input
                                    id="contactEmail"
                                    name="contactEmail"
                                    type="email"
                                    value={referralData.contactEmail}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="contactPhone">Contact Phone</Label>
                                  <Input
                                    id="contactPhone"
                                    name="contactPhone"
                                    type="tel"
                                    value={referralData.contactPhone}
                                    onChange={handleReferralInputChange}
                                    className="mt-1"
                                  />
                                </div>
                              </div>

                              <div className="mt-4">
                                <Label htmlFor="hiringNeeds">Hiring Needs / Open Positions *</Label>
                                <Textarea
                                  id="hiringNeeds"
                                  name="hiringNeeds"
                                  required
                                  rows={3}
                                  placeholder="e.g., Looking for maintenance technicians, expanding operations team, need engineers"
                                  value={referralData.hiringNeeds}
                                  onChange={handleReferralInputChange}
                                  className="mt-1"
                                />
                              </div>
                            </div>

                            {/* Additional Info */}
                            <div>
                              <Label htmlFor="additionalInfo">Additional Information</Label>
                              <Textarea
                                id="additionalInfo"
                                name="additionalInfo"
                                rows={3}
                                placeholder="Anything else we should know? How did you hear about their hiring needs?"
                                value={referralData.additionalInfo}
                                onChange={handleReferralInputChange}
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label htmlFor="referral-howDidYouHear">How did you hear about Service2Success?</Label>
                              <select
                                id="referral-howDidYouHear"
                                name="howDidYouHear"
                                value={referralData.howDidYouHear}
                                onChange={handleReferralInputChange}
                                data-testid="referral-how-did-you-hear"
                                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                              >
                                <option value="">Select an option</option>
                                <option value="google">Google Search</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="facebook">Facebook</option>
                                <option value="instagram">Instagram</option>
                                <option value="referral">Friend/Family Referral</option>
                                <option value="veteran-org">Veteran Organization</option>
                                <option value="job-board">Job Board</option>
                                <option value="military-transition">Military Transition Program</option>
                                <option value="employer-referral">Employer Referral</option>
                                <option value="networking-event">Networking Event</option>
                                <option value="other">Other</option>
                              </select>
                            </div>

                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] text-lg py-6"
                            >
                              {isSubmitting ? 'Submitting...' : (
                                <>
                                  Submit Company Referral
                                  <Users className="ml-2" size={20} />
                                </>
                              )}
                            </Button>

                            <p className="text-sm text-gray-500 text-center">
                              We'll reach out to the company and keep you updated on the referral status.
                            </p>
                          </form>
                        ) : (
                          <form onSubmit={handleSubmit} className="space-y-6">
                            {submitError && (
                              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start space-x-2">
                                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                                <span>{submitError}</span>
                              </div>
                            )}
                            
                            <div>
                              <Label htmlFor="name">Full Name *</Label>
                              <Input
                                id="name"
                                name="name"
                                type="text"
                                required
                                value={formData.name}
                                onChange={handleInputChange}
                                className="mt-1"
                              />
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="email">Email Address *</Label>
                                <Input
                                  id="email"
                                  name="email"
                                  type="email"
                                  required
                                  value={formData.email}
                                  onChange={handleInputChange}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label htmlFor="phone">Phone Number *</Label>
                                <Input
                                  id="phone"
                                  name="phone"
                                  type="tel"
                                  required
                                  value={formData.phone}
                                  onChange={handleInputChange}
                                  className="mt-1"
                                />
                              </div>
                            </div>

                            {formType === 'hire' && (
                              <div>
                                <Label htmlFor="company">Company Name *</Label>
                                <Input
                                  id="company"
                                  name="company"
                                  type="text"
                                  required
                                  value={formData.company}
                                  onChange={handleInputChange}
                                  className="mt-1"
                                />
                              </div>
                            )}

                            <div>
                              <Label htmlFor="message">
                                {formType === 'hire' && 'Tell us about your hiring needs *'}
                                {formType === 'general' && 'Your Message *'}
                              </Label>
                              <Textarea
                                id="message"
                                name="message"
                                required
                                rows={6}
                                value={formData.message}
                                onChange={handleInputChange}
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label htmlFor="contact-howDidYouHear">How did you hear about Service2Success?</Label>
                              <select
                                id="contact-howDidYouHear"
                                name="howDidYouHear"
                                value={formData.howDidYouHear}
                                onChange={handleInputChange}
                                data-testid="contact-how-did-you-hear"
                                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                              >
                                <option value="">Select an option</option>
                                <option value="google">Google Search</option>
                                <option value="linkedin">LinkedIn</option>
                                <option value="facebook">Facebook</option>
                                <option value="instagram">Instagram</option>
                                <option value="referral">Friend/Family Referral</option>
                                <option value="veteran-org">Veteran Organization</option>
                                <option value="job-board">Job Board</option>
                                <option value="military-transition">Military Transition Program</option>
                                <option value="employer-referral">Employer Referral</option>
                                <option value="networking-event">Networking Event</option>
                                <option value="other">Other</option>
                              </select>
                            </div>

                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              className="w-full bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564] text-lg py-6"
                            >
                              {isSubmitting ? (
                                'Submitting...'
                              ) : (
                                <>
                                  {formType === 'hire' && 'Submit Inquiry'}
                                  {formType === 'general' && 'Send Message'}
                                  <Send className="ml-2" size={20} />
                                </>
                              )}
                            </Button>
                          </form>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule a Meeting Section */}
      <section className="py-20 bg-gray-50 border-t-4 border-[#d4a574]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-4">
                <Calendar className="text-[#0a1628]" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-[#0a1628] mb-4">
                Schedule a Free Discovery Call
              </h2>
              <p className="text-lg text-gray-700 mb-2">
                Book a 15-minute consultation directly on my calendar
              </p>
              <p className="text-gray-600">
                Available Monday-Friday, 8:00 AM - 5:00 PM Eastern Time
              </p>
            </div>

            <Card className="border-2 border-[#d4a574] overflow-hidden">
              <CardContent className="p-0">
                {/* Calendly Inline Widget */}
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/rich-s2stalent?hide_gdpr_banner=1&primary_color=d4a574"
                  style={{ minWidth: '320px', height: '700px' }}
                ></div>
              </CardContent>
            </Card>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Choose from 15-minute, 30-minute, or 1-hour time slots based on your needs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional CTA */}
      <section className="py-16 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Prefer to Call?
            </h2>
            <p className="text-xl text-gray-300 mb-6">
              Reach out directly for immediate assistance
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a href="tel:513-668-0344" className="text-3xl font-bold text-[#d4a574] hover:text-[#c49564]">
                513-668-0344
              </a>
              <span className="hidden sm:inline text-gray-500">|</span>
              <a href="mailto:rich@s2stalent.com" className="text-xl text-[#d4a574] hover:text-[#c49564]">
                rich@s2stalent.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
